import type { CSSProperties, ReactNode } from 'react';

type IconName =
  | 'home' | 'inbox' | 'play' | 'history' | 'server' | 'users' | 'sliders' | 'user'
  | 'search' | 'chevdown' | 'chevright' | 'chevleft' | 'close' | 'plus' | 'flag'
  | 'alert' | 'check' | 'eye' | 'download' | 'pin' | 'refresh' | 'filter'
  | 'activity' | 'map' | 'location' | 'clock' | 'doc' | 'money' | 'pulse'
  | 'ext' | 'arrowup' | 'arrowdown' | 'dot3' | 'settings' | 'bell' | 'logout'
  | 'radio' | 'car' | 'handshake' | 'shield';

interface IconProps {
  name: IconName;
  size?: number;
  stroke?: number;
  color?: string;
  style?: CSSProperties;
}

export function Icon({ name, size = 16, stroke = 1.6, color = 'currentColor', style }: IconProps) {
  const paths: Record<IconName, ReactNode> = {
    home: <><path d="M3 10.5L10 4l7 6.5"/><path d="M5 9.5V17h10V9.5"/></>,
    inbox: <><rect x="3" y="3.5" width="14" height="13" rx="1.5"/><path d="M3 11h4l1 2h4l1-2h4"/></>,
    play: <><path d="M5 4l11 6-11 6V4z" fill={color} stroke="none"/></>,
    history: <><circle cx="10" cy="10" r="7"/><path d="M10 6v4l2.5 2"/></>,
    server: <><rect x="3" y="4" width="14" height="5" rx="1"/><rect x="3" y="11" width="14" height="5" rx="1"/><circle cx="6" cy="6.5" r=".5" fill={color}/><circle cx="6" cy="13.5" r=".5" fill={color}/></>,
    users: <><circle cx="7.5" cy="7" r="3"/><path d="M2.5 16c.5-2.5 2.5-4.5 5-4.5s4.5 2 5 4.5"/><circle cx="14" cy="6" r="2.2"/><path d="M13 11.5c2 0 4 1.5 4.5 4"/></>,
    sliders: <><path d="M3 6h7"/><path d="M14 6h3"/><path d="M3 14h2"/><path d="M9 14h8"/><circle cx="12" cy="6" r="1.6"/><circle cx="7" cy="14" r="1.6"/></>,
    user: <><circle cx="10" cy="7" r="3"/><path d="M4 17c0-3 2.5-5.5 6-5.5s6 2.5 6 5.5"/></>,
    search: <><circle cx="9" cy="9" r="5"/><path d="M13 13l4 4"/></>,
    chevdown: <><path d="M5 8l5 5 5-5"/></>,
    chevright: <><path d="M8 5l5 5-5 5"/></>,
    chevleft: <><path d="M12 5l-5 5 5 5"/></>,
    close: <><path d="M5 5l10 10M15 5L5 15"/></>,
    plus: <><path d="M10 4v12M4 10h12"/></>,
    flag: <><path d="M5 17V3"/><path d="M5 3h9l-2 3 2 3H5"/></>,
    alert: <><path d="M10 3l8 14H2L10 3z"/><path d="M10 8v4M10 14.5v.01"/></>,
    check: <><path d="M4 10l4 4 8-8"/></>,
    eye: <><path d="M2 10s3-5.5 8-5.5S18 10 18 10s-3 5.5-8 5.5S2 10 2 10z"/><circle cx="10" cy="10" r="2.5"/></>,
    download: <><path d="M10 3v10M5 9l5 4 5-4"/><path d="M3 17h14"/></>,
    pin: <><path d="M8 3h4l-.5 5L14 11H6l2.5-3z"/><path d="M10 11v6"/></>,
    refresh: <><path d="M3 10a7 7 0 0 1 12-5l2 2"/><path d="M17 3v4h-4"/><path d="M17 10a7 7 0 0 1-12 5l-2-2"/><path d="M3 17v-4h4"/></>,
    filter: <><path d="M3 4h14l-5 7v4l-4 2v-6L3 4z"/></>,
    activity: <><path d="M2 10h4l2-5 4 10 2-5h4"/></>,
    map: <><path d="M2 5l5-2 6 2 5-2v12l-5 2-6-2-5 2V5z"/><path d="M7 3v12M13 5v12"/></>,
    location: <><path d="M10 18s6-5.5 6-10a6 6 0 1 0-12 0c0 4.5 6 10 6 10z"/><circle cx="10" cy="8" r="2.2"/></>,
    clock: <><circle cx="10" cy="10" r="7"/><path d="M10 6v4l2.5 2"/></>,
    doc: <><path d="M5 2h7l3 3v13H5V2z"/><path d="M12 2v3h3"/></>,
    money: <><circle cx="10" cy="10" r="7"/><path d="M10 6v8M7.5 8.5c0-1 1-1.5 2.5-1.5s2.5.5 2.5 1.5-1 1.3-2.5 1.5-2.5.5-2.5 1.5 1 1.5 2.5 1.5 2.5-.5 2.5-1.5"/></>,
    pulse: <><path d="M2 10h3l2-4 3 8 2-4h6"/></>,
    ext: <><path d="M10 4h6v6"/><path d="M16 4l-7 7"/><path d="M14 11v5H4V6h5"/></>,
    arrowup: <><path d="M10 16V4M5 9l5-5 5 5"/></>,
    arrowdown: <><path d="M10 4v12M5 11l5 5 5-5"/></>,
    dot3: <><circle cx="5" cy="10" r="1.3" fill={color}/><circle cx="10" cy="10" r="1.3" fill={color}/><circle cx="15" cy="10" r="1.3" fill={color}/></>,
    settings: <><circle cx="10" cy="10" r="2.5"/><path d="M10 3v2M10 15v2M3 10h2M15 10h2M5 5l1.5 1.5M13.5 13.5L15 15M5 15l1.5-1.5M13.5 6.5L15 5"/></>,
    bell: <><path d="M5 8a5 5 0 0 1 10 0c0 4 1.5 5.5 1.5 5.5h-13S5 12 5 8z"/><path d="M8 16a2 2 0 0 0 4 0"/></>,
    logout: <><path d="M11 4H4v12h7"/><path d="M9 10h9"/><path d="M15 7l3 3-3 3"/></>,
    radio: <><circle cx="10" cy="10" r="2.2"/><path d="M5.5 5.5a6.5 6.5 0 0 0 0 9M14.5 5.5a6.5 6.5 0 0 1 0 9"/><path d="M3 3a10 10 0 0 0 0 14M17 3a10 10 0 0 1 0 14"/></>,
    car: <><path d="M3 13v-2l2-5h10l2 5v2"/><path d="M2 13h16v3H2z"/><circle cx="6" cy="15" r="1.3"/><circle cx="14" cy="15" r="1.3"/></>,
    handshake: <><path d="M2 10l4-3 3 1 2-1 5 3-2 2-2-1-2 2-2-1-3 2-3-4z"/></>,
    shield: <><path d="M10 3l6 2v5c0 4-3 6.5-6 7.5-3-1-6-3.5-6-7.5V5l6-2z"/></>,
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke={color}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, ...style }}
    >
      {paths[name] || null}
    </svg>
  );
}
